// ============================================================
// constants/physics_consts.js
// EV Powertrain Simulator — 공유 물리 상수 단일 출처 (Single Source of Truth)
//
// [Fix-4]  ROAD_MU를 simulation.js / dynamics.js / tire.js에서 각각 선언하던
//          방식을 제거하고 이 파일에서 단일 export로 통합.
// [Fix-C1] G_GRAVITY를 9.81 → 9.80665 (SI 표준값)으로 통일.
//          math_utils.rhoCorrected가 사용하던 로컬 9.80665 리터럴과 값 일치.
//          battery.js / dynamics.js / drive_control.js / vehicle.js의
//          인라인 9.81 리터럴을 모두 이 상수로 교체.
// [Fix-C2] R_GAS, F_FARADAY를 battery.js 인라인 선언 4곳에서 제거하고
//          이 파일을 단일 출처로 통합.
//
// 이 파일에 추가할 대상:
//   - 여러 core/ 파일에서 동일 값으로 중복 선언되는 물리 상수
//   - globalThis.XXX || { ... } 패턴으로 방어적 복사를 사용하던 상수
// ============================================================

/**
 * ROAD_MU — 노면 조건별 최대 Coulomb 마찰계수
 * 출처: ISO 15037-1 / SAE J2246 실측 기반 대표값
 * 단위: 무차원 (-)
 * 사용처: dynamics.js traction limit 계산, simulation.js F_trac_lim
 */
export const ROAD_MU = {
  dry:  1.0,   // 건조 아스팔트 — 양호한 신품 타이어 기준
  wet:  0.7,   // 젖은 노면 — 수막 형성 전 마찰 유지 구간
  snow: 0.4,   // 눈길 — 압설(packed snow) 기준
  ice:  0.3,   // 빙판 — 결빙 노면 (블랙아이스 포함 최악 추정값)
};

/**
 * G_GRAVITY — 표준 중력가속도
 * 출처: ISO 80000-3 / BIPM — 국제단위계(SI) 정의값
 * 단위: m/s²
 * 사용처: dynamics.js F_grade·F_roll, battery.js Ri 보정, vehicle.js 가속도 산정
 * 주의: [Fix-C1] 9.81 → 9.80665 로 통일. 이 파일 외에 리터럴 사용 금지.
 */
export const G_GRAVITY = 9.80665;

/**
 * 표준 대기 조건 — ISO 2533(국제표준대기 ISA) 해발 0m 기준
 * 사용처: math_utils.rhoCorrected() 기준값, 열관리 초기조건
 */
export const STD_RHO   = 1.225;   // 공기밀도 [kg/m³] @ 15°C, 1 atm (ISA 해면 조건)
export const STD_T_AMB = 25;      // 기준 주변온도 [°C] — 시뮬레이션 기본값

/**
 * 열역학·전기화학 기본 상수
 * 출처: NIST CODATA 2018 권고값
 * 사용처: battery.js Arrhenius 열화 모델 (Ea/R·T 지수항), SOH 계산
 */
export const R_GAS     = 8.314;   // 기체상수 [J/(mol·K)] — 이상기체 상수
export const F_FARADAY = 96485;   // Faraday 상수 [C/mol] — 전자 1 mol당 전하량

// ── [P-3-Fix] 국가별 전력망 탄소 배출 계수 [g CO₂/kWh] ──────────────────
// 출처: IEA "Electricity 2024" / 각국 공식 발표 (기준 연도 2022–2023)
// fallback을 한국 고정값(459)에서 IEA 세계 평균으로 교체하여 국가별 ×2 오차 해소.
//
// 이전: simulation.js 내 `|| 459` 리터럴 → 한국 전용, 국가 불문 적용 → P-3 오류
// 수정: GRID_EMISSION_FACTORS 테이블 + GRID_EF_DEFAULT(세계 평균) 로 대체
//       setSSTConstants() 주입으로 simulation.js zero-import 제약 준수
/**
 * GRID_EMISSION_FACTORS — 국가별 전력망 탄소 배출 계수
 * 출처: IEA "Electricity 2024" / 각국 공식 발표 (기준 연도 2022–2023)
 * 단위: g CO₂/kWh
 * 사용처: simulation.js CO₂ 배출량 계산 (총 전력 소비량 × 국가 계수)
 * 주의: [Fix-P-3] 한국 고정값(459) 제거 후 국가별 선택 방식으로 전환.
 *       시뮬레이션에서는 setSSTConstants()로 주입하여 zero-import 제약 준수.
 */
export const GRID_EMISSION_FACTORS = {
  KR:    459,  // 대한민국 (한국전력 2023 잠정)
  JP:    432,  // 일본     (METI 2023)
  US:    386,  // 미국     (EPA eGRID 2022)
  DE:    385,  // 독일     (UBA 2023)
  GB:    233,  // 영국     (DESNZ 2023)
  CN:    581,  // 중국     (MEE 2022)
  FR:     57,  // 프랑스   (RTE 2023, 원자력 비중 70%+)
  NO:     26,  // 노르웨이 (NVE 2023, 수력 95%)
  AU:    490,  // 호주     (DCCEEW 2023)
  IN:    632,  // 인도     (CEA 2023)
  WORLD: 436,  // 세계 평균 (IEA 2022) — 기본값
};

/** 기본 탄소 배출 계수 — IEA 세계 평균 [g CO₂/kWh]
 *  simulation.js `|| 459` 리터럴을 이 값으로 교체 (P-3 Fix).
 *  국가별 정확한 값은 GRID_EMISSION_FACTORS 테이블 참조. */
export const GRID_EF_DEFAULT = GRID_EMISSION_FACTORS.WORLD; // 436